import axios from 'axios';
import React from 'react';
import { Form, FormControl, FormGroup, Radio } from 'react-bootstrap';
import Swal from 'sweetalert2';
import upperFirst from 'lodash/upperFirst';
import { Loading } from '../components/Loading';
import { getBongoUrl } from '../../common/utils/bongo';

type FieldsSelectionContainerProps = {
  tenant: number;
  field_pulled: UnknownObject;
  object: string;
  integration: string;
  email: string;
};

type FieldsSelectionContainerState = {
  objects_list: string[];
  field_list: UnknownObject[];
  object: string;
  period: string;
  since: string;
  json: string;
  using_mdi?: boolean;
  integration_loading: boolean;
};

const OBJECT_LIST: Record<string, string[]> = {
  hubspot: ['contact', 'deal', 'company'],
  snowflake: ['contact', 'account', 'event'],
  bigquery: ['contact', 'account', 'event'],
};

export default class FieldSelectionContainer extends React.Component<
  FieldsSelectionContainerProps,
  FieldsSelectionContainerState
> {
  constructor(props: FieldsSelectionContainerProps) {
    super(props);

    const objects_list = OBJECT_LIST[this.props.integration] || [
      'Lead',
      'Contact',
      'Account',
      'Opportunity',
      'Task',
      'Campaign',
      'CampaignMember',
      'Custom',
    ];

    this.state = {
      objects_list,
      field_list: [],
      object: props.object,
      period: '1year',
      since: '',
      json: '',
      using_mdi: false,
      integration_loading: true,
    };
    // eslint-disable-next-line
    this.requestFields(props.object);

    this.generateDemand = this.generateDemand.bind(this);
    this.fetchIntegrationDetails = this.fetchIntegrationDetails.bind(this);
  }

  async componentDidMount() {
    await this.fetchIntegrationDetails();
  }

  async fetchIntegrationDetails() {
    try {
      const response = await axios.get(
        `${getBongoUrl()}/v1/org/${this.props.tenant}/integrations/${this.props.integration}`
      );
      this.setState({
        using_mdi: response.data.using_mdi || false,
        integration_loading: false,
      });
    } catch (error) {
      console.error('Failed to fetch integration details:', error);
      this.setState({
        using_mdi: false,
        integration_loading: false,
      });
    }
  }

  generateDemand() {
    return {
      email: this.props.email,
      tenant: this.props.tenant,
      connector: this.props.integration,
      object: this.state.object,
      list: this.state.field_list
        .filter((field) => field.will_be_pulled && !field.previously_pulled)
        .map((field) => field.name),
      period: this.state.period === 'custom' ? this.state.since : null,
    };
  }

  generateRecap() {
    const data = this.generateDemand();
    let list_update = '';
    let counterp = 0;
    // eslint-disable-next-line
    for (const field of this.state.field_list) {
      if (field.will_be_pulled && !field.previously_pulled) {
        list_update += `<div>    - ${field.name}</div>`;
        counterp += 1;
      }
    }
    const html_text = `
    <p>You are about to pull <b>${counterp}</b> new field(s) for this tenant.</p>
    <p>You will receive an email when executed!</p>
    <div>[REQUEST BY]   ${data.email}</div>
    <div>[TENANT]   ${data.tenant}</div>
    <div>[CONNECTOR]   ${data.connector}</div>
    <div>[OBJECT]   ${data.object}</div>
    ${list_update}
    <div>[PERIOD]</div><div> ${data.period ? data.period : '1 year'}</div>`;

    return {
      added_fields: counterp,
      html: html_text,
    };
  }

  displayRecap() {
    const recap = this.generateRecap();

    let alert_type: 'warning' | 'info' = 'warning';
    let alert_title = 'Please verify the fields to pull';
    if (recap.added_fields === 0) {
      alert_type = 'info';
      alert_title = 'No differences with current pull configuration';
      recap.html = '';
    }

    Swal.fire({
      title: alert_title,
      html: recap.html,
      icon: alert_type,
      showCancelButton: true,
      confirmButtonText: 'Pull data',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    }).then((result: any) => {
      // eslint-disable-next-line
      if (result.value) this.triggerPullAfterConfirmation();
    });
  }

  async triggerPullAfterConfirmation() {
    try {
      Swal.fire({
        title: 'Sending request.',
        icon: 'info',
        allowOutsideClick: () => !Swal.isLoading(),
      });
      Swal.showLoading();

      const data = this.generateDemand();

      // For MDI integrations, set period to null
      if (this.state.using_mdi) {
        data.period = null;
      }

      const {
        status,
        data: responseData,
      } = await axios.post(
        `${getBongoUrl()}/v1/org/${this.props.tenant}/integrations/${this.props.integration}/pull/trigger`,
        { data }
      );

      if (status < 200 || status > 300) {
        throw new Error(
          `The request failed with code ${status}\n ${responseData}`
        );
      }

      await Swal.fire(
        'Request sent!',
        'The pull will start shortly.<br />You will receive an email when finished',
        'success'
      );
    } catch (err) {
      await Swal.fire(
        'Request failed!',
        `Something went wrong:<br />${err.message}`,
        'error'
      );
    }
  }

  async triggerPull() {
    // For MDI integrations, use the new confirmation dialog
    if (this.state.using_mdi) {
      // Get the new fields to be pulled
      const newFields = this.state.field_list.filter(
        (field) => field.will_be_pulled && !field.previously_pulled
      );

      if (newFields.length === 0) {
        await Swal.fire(
          'No new fields selected',
          'Please select at least one new field to pull.',
          'info'
        );
        return;
      }

      // Show confirmation with field list for MDI
      const fieldsList = newFields
        .map((field) => `• ${field.name}`)
        .join('<br>');
      const result = await Swal.fire({
        title: `Pull ${newFields.length} new ${this.props.object} field${
          newFields.length > 1 ? 's' : ''
        }?`,
        html: `
          <p>You are about to pull the following new field${
            newFields.length > 1 ? 's' : ''
          }:</p>
          <div style="text-align: left; margin: 15px 0; padding: 10px; background: #f8f9fa; border-radius: 5px;">
            ${fieldsList}
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, pull data',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
      });

      if (!result.isConfirmed) {
        return;
      }

      // Proceed with the pull for MDI
      await this.triggerPullAfterConfirmation();
    } else {
      // For non-MDI integrations, use the original displayRecap
      this.displayRecap();
    }
  }

  async requestFields(object: string) {
    const res = await axios.get(
      `${getBongoUrl()}/v1/org/${this.props.tenant}/integrations/${
        this.props.integration
      }/data/${object.toLowerCase()}/fields`,
      {}
    );
    const list = res.data.fields;

    // eslint-disable-next-line
    for (const item of list) {
      if (this.props.field_pulled[object]) {
        // eslint-disable-next-line
        for (const pulled of this.props.field_pulled[object].attributes) {
          if (item.name.toLowerCase() === pulled.name.toLowerCase())
            // eslint-disable-next-line
            item.will_be_pulled = item.previously_pulled = true;
        }
        if (!item.will_be_pulled)
          // eslint-disable-next-line
          item.will_be_pulled = item.previously_pulled = false;
      }
    }
    this.setState({ field_list: list, object });
  }

  // Action handlers

  handleUpdateIsPulled(id: number) {
    const copy = this.state.field_list.slice();
    copy[id].will_be_pulled = !copy[id].will_be_pulled;
    this.setState({ field_list: copy });
  }

  handleObjectChange() {
    this.setState({ field_list: [] });
    const curr_object = (document.getElementById(
      'object_selector'
    ) as HTMLSelectElement).value;
    const object = curr_object || 'Lead';
    if (object === 'Custom') {
      this.setState({ object: 'Custom' });
    } else {
      // eslint-disable-next-line
      this.requestFields(object);
    }
  }

  handleChangePeriod(period: string) {
    this.setState({
      period,
    });
  }

  handleChangeSince(since: string) {
    this.setState({
      since,
    });
  }

  render() {
    const isLoading =
      this.state.integration_loading || this.state.field_list.length === 0;

    return (
      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
        <div>
          <h2>Configure pulled fields</h2>
          <p>
            Choose which data fields of the selected object you want to pull
            from your {upperFirst(this.props.integration)}.
          </p>
        </div>

        <div>
          <select
            id="object_selector"
            style={{ width: '100%', maxWidth: '110px' }}
            className="form-control"
            value={this.state.object}
            onChange={() => this.handleObjectChange()}
          >
            {this.state.objects_list.map((object) => (
              <option key={object} value={object}>
                {object}
              </option>
            ))}
          </select>
        </div>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <div
              style={{
                height: '400px',
                overflowY: 'scroll',
                position: 'relative',
                borderRadius: '0.375rem',
                border: '1px solid rgba(0, 0, 0, 0.125)',
              }}
            >
              <table className="table mx-auto" style={{ tableLayout: 'auto' }}>
                <thead>
                  <tr
                    className="sticky-top"
                    style={{ backgroundColor: 'white' }}
                  >
                    <th>Pull?</th>
                    <th>Name</th>
                    <th>Label</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {this.state.field_list.map(
                    (
                      { label, type, name, will_be_pulled, previously_pulled },
                      id
                    ) => (
                      <tr key={name}>
                        <td className="td_little">
                          <input
                            type="checkbox"
                            disabled={previously_pulled}
                            defaultChecked={will_be_pulled}
                            onClick={() => this.handleUpdateIsPulled(id)}
                          />
                        </td>
                        <td>{name}</td>
                        <td>{label}</td>
                        <td>{type}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div>
              {this.state.json && (
                <div>
                  <textarea
                    className="textarea-big"
                    readOnly
                    value={this.state.json}
                  />
                </div>
              )}
            </div>
            <div className="tw-flex tw-gap-2">
              {this.state.using_mdi && (
                <button
                  className="btn btn-primary"
                  onClick={() => this.triggerPull()}
                >
                  Save and Pull new fields
                </button>
              )}
            </div>

            {!this.state.using_mdi && (
              <div>
                <h2 className="mt-2">Pull new fields selected automatically</h2>
                <Form inline>
                  <FormGroup>
                    <Radio
                      name="radioGroup"
                      inline
                      checked={this.state.period === '1year'}
                      onChange={() => this.handleChangePeriod('1year')}
                    >
                      <span style={{ marginLeft: '5px' }}> 1 year of data</span>
                    </Radio>
                    <Radio
                      name="radioGroup"
                      inline
                      style={{ marginLeft: '15px' }}
                      checked={this.state.period === 'custom'}
                      onChange={() => this.handleChangePeriod('custom')}
                    >
                      <span style={{ marginLeft: '5px' }}>since</span>
                    </Radio>
                    <FormControl
                      type="text"
                      placeholder="2019-01-01"
                      style={{ marginLeft: '5px' }}
                      value={this.state.since}
                      onChange={(e: any) =>
                        this.handleChangeSince(e.target.value)
                      }
                    />
                  </FormGroup>
                </Form>

                <button
                  className="btn btn-primary"
                  disabled={
                    this.state.period === 'custom' &&
                    this.state.since.trim().length === 0
                  }
                  onClick={() => this.displayRecap()}
                >
                  Pull data
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}
